import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { pick } from 'lodash';

import { FormWrapper } from 'components/Modal/styles';
import { Form } from 'components';
import ModalForm from 'components/ModalForm/ModalForm';
import {
  useCreateParentProduct,
  useFetchProductCategoriesInfinite,
  useUpdateParentProduct,
} from 'api/products/hooks';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { mapInitialValues } from '../../utils/parentProductFormUtils';
import validationSchemas from './schemas';

const productFormWrapperStyle = {
  width: '100%',
};

const ParentProductForm = ({ type = 'create', initialValues, onRequiredDataLoad }) => {
  const { t } = useTranslation(['pages', 'app']);
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const mappedInitialValues = useMemo(() => mapInitialValues(initialValues), [initialValues]);
  const [categoriesSearch, setCategoriesSearch] = useState('');

  const { mutate: createParentProduct, isPending: isCreating } = useCreateParentProduct();
  const { mutate: updateParentProduct, isPending: isUpdating } = useUpdateParentProduct();

  const {
    data: categoriesData,
    fetchNextPage: fetchMoreCategories,
    hasNextPage: hasMoreCategories,
    isLoading: isLoadingCategories,
    isFetchingNextPage: isFetchingMoreCategories,
  } = useFetchProductCategoriesInfinite({
    pageSize: 30,
    search: categoriesSearch,
    filters: {
      region: selectedRegion,
    },
    ordering: 'name',
  });

  const categories = categoriesData ? categoriesData.pages.flatMap((page) => page.results) : [];

  useEffect(() => {
    if (onRequiredDataLoad && !isLoadingCategories) {
      const missingItems = categories.length === 0 ? ['category'] : [];
      onRequiredDataLoad(missingItems);
    }
  }, [categories.length, isLoadingCategories, onRequiredDataLoad]);

  return (
    <ModalForm
      initialValues={
        pick(mappedInitialValues, ['uid', 'name', 'description', 'details', 'category_uid']) || {
          name: '',
          description: '',
          details: '',
          category_uid: '',
        }
      }
      onSubmit={({ uid, name, description, details, category_uid }) =>
        type === 'update'
          ? updateParentProduct({
              uid,
              name,
              description,
              details,
              region_uid: selectedRegion,
              category_uid,
            })
          : createParentProduct({
              name,
              description,
              details,
              region_uid: selectedRegion,
              category_uid,
            })
      }
      validationSchema={validationSchemas.parent_product}
      cancelLabel={t('app:cancel')}
      submitLabel={t(`app:${type}`)}
      pristine
      isLoading={isCreating || isUpdating}
      ignoreClickedState
      isClosePopupOn={false}
    >
      <FormWrapper style={productFormWrapperStyle}>
        <Form.GroupVertical required label={t('createProduct.name')}>
          <Form.Input name="name" type="text" />
        </Form.GroupVertical>
        <Form.GroupVertical required label={t('createProduct.description')}>
          <Form.MarkdownEditor name="description" height={200} />
        </Form.GroupVertical>
        <Form.GroupVertical required label={t('createProduct.details')}>
          <Form.MarkdownEditor name="details" height={200} />
        </Form.GroupVertical>
        <Form.GroupVertical required label={t('createProduct.category')}>
          <Form.MultiSelectInfinite
            options={categories.map((category) => ({
              value: category.uid,
              label: category.name.toUpperCase(),
            }))}
            initialOption={
              mappedInitialValues.category && {
                value: mappedInitialValues.category.uid,
                label: mappedInitialValues.category.name.toUpperCase(),
              }
            }
            hasMoreOptions={hasMoreCategories}
            loadMoreOptions={fetchMoreCategories}
            isLoadingOptions={isLoadingCategories}
            isLoadingMoreOptions={isFetchingMoreCategories}
            onSearch={setCategoriesSearch}
            name="category_uid"
            isMulti={false}
          />
        </Form.GroupVertical>
      </FormWrapper>
    </ModalForm>
  );
};

ParentProductForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  onRequiredDataLoad: PropTypes.func,
};

export default ParentProductForm;
